import styled, { keyframes, css } from 'styled-components'

import { media } from '../../../utils/mediaQuery'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const EditorContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 16px;
  width: 320px;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 6px;
  z-index: 1000;
  max-height: calc(100vh - 200px);

  ${media.tabletDown} {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    max-height: 100vh;
    margin-top: 0;
    border-radius: 0;
    z-index: 9999;
    padding: 20px;
    display: flex;
    flex-direction: column;
  }

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c4c4c4;
    border-radius: 3px;

    &:hover {
      background: #a0a0a0;
    }
  }
`

export const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
  }

  ${media.tabletDown} {
    padding-bottom: 16px;
    margin-bottom: 24px;
    border-bottom: 2px solid #e5e7eb;

    h3 {
      font-size: 18px;
    }
  }
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 23px;
  height: 23px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }

  /* Mobile: Ensure 44px minimum touch target */
  ${media.tabletDown} {
    width: ${({ theme }) => theme.touchTargets.minimum};
    height: ${({ theme }) => theme.touchTargets.minimum};
    font-size: 28px;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: none;
      color: #6b7280;
    }

    &:active {
      background: #f3f4f6;
      color: #1f2937;
    }
  }
`

export const TagsList = styled.div`
  margin-bottom: 20px;
  flex-shrink: 0;

  h4 {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  ${media.tabletDown} {
    margin-bottom: 20px;
  }
`

export const TagItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    transform: translateX(2px);
  }
`

export const TagColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const TagName = styled.span`
  flex: 1;
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
`

export const TagType = styled.span<{ $isPrimary?: boolean }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
  ${({ $isPrimary }) =>
    $isPrimary
      ? css`
          background: #dbeafe;
          color: #1e40af;
        `
      : css`
          background: #f3f4f6;
          color: #6b7280;
        `}
`

export const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.2s;
  opacity: 0.6;

  &:hover {
    opacity: 1;
    background: #fee2e2;
  }

  /* Mobile: Ensure 44px minimum touch target */
  ${media.tabletDown} {
    width: ${({ theme }) => theme.touchTargets.minimum};
    height: ${({ theme }) => theme.touchTargets.minimum};
    opacity: 1;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    opacity: 1;

    &:hover {
      background: none;
    }

    &:active {
      background: #fee2e2;
    }
  }
`

export const SearchSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  h4 {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  transition: all 0.2s;
  margin-bottom: 10px;

  &:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

export const ExistingTagsList = styled.div`
  max-height: 180px;
  overflow-y: auto;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px;
  flex-shrink: 1;

  ${media.tabletDown} {
    flex: 1;
    min-height: 200px;
    max-height: none;
  }

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 2px;
  }
`

export const ExistingTagItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 3px;

  ${({ $selected }) =>
    $selected
      ? css`
          background: #dbeafe;
          border: 1px solid #60a5fa;
        `
      : css`
          background: transparent;
          border: 1px solid transparent;

          &:hover {
            background: #f9fafb;
          }
        `}
`

export const ExistingTagColor = styled.div<{ $color: string }>`
  width: 11px;
  height: 11px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const ExistingTagName = styled.span`
  font-size: 11px;
  color: #1f2937;
  font-weight: 500;
`

export const NewTagSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  h4 {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }
`

export const TagInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  transition: all 0.2s;
  margin-bottom: 12px;

  &:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

export const ColorPicker = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`

export const ColorOption = styled.div<{ $color: string; $selected: boolean }>`
  width: 23px;
  height: 23px;
  border-radius: 5px;
  background: ${({ $color }) => $color};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  ${({ $selected }) =>
    $selected &&
    css`
      box-shadow:
        0 0 0 2px white,
        0 0 0 4px #60a5fa;
      transform: scale(1.1);
    `}

  &:hover {
    transform: scale(1.1);
  }
`

export const TagTypeToggle = styled.div`
  margin-bottom: 12px;

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #4b5563;
    cursor: pointer;

    input[type='checkbox'] {
      width: 12px;
      height: 12px;
      cursor: pointer;
    }
  }
`

export const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  flex-shrink: 0;

  ${media.tabletDown} {
    gap: 12px;
    padding-top: 12px;
  }
`

export const Button = styled.button`
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  outline: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${media.tabletDown} {
    padding: 12px 20px;
    font-size: 14px;
  }
`

export const AddButton = styled(Button)`
  background: #10b981;
  color: white;

  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  }
`

export const SaveButton = styled(Button)`
  background: #3b82f6;
  color: white;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
`

export const CancelButton = styled(Button)`
  background: #f3f4f6;
  color: #4b5563;

  &:hover {
    background: #e5e7eb;
  }
`

export const CreateNewButton = styled(Button)`
  background: white;
  color: #06ade0;
  border: 1px solid #06ade0;

  &:hover {
    background: #eff6ff;
  }
`

export const WarningMessage = styled.div`
  padding: 6px 10px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 5px;
  color: #92400e;
  font-size: 11px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '⚠️';
  }
`

export const EmptyMessage = styled.div`
  padding: 12px;
  text-align: center;
  color: #9ca3af;
  font-size: 11px;
  font-style: italic;
`

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  margin: 16px auto;
`

export const MobileBackdrop = styled.div`
  display: none;

  ${media.tabletDown} {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
    animation: ${fadeIn} 0.2s ease;
  }
`
